import { useState, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import Button from "../../../../shared/ui/button/Button";
import {
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  ShieldCheck,
  User as UserIcon,
  Mail,
  ExternalLink,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import {
  acceptCenterInvitation,
  hasSignedInUser,
  refreshIdToken,
  reloadEmailVerified,
} from "../../application/session";
import { openSession } from "../../application/signIn";
import {
  enableMfaForSignedInUser,
  registerTherapist,
  sendVerificationEmail,
  sendWelcomeEmail,
} from "../../application/signUp";
import { useTherapist } from "../../../../context/useTherapist";
import GoogleSignInButton from "./GoogleSignInButton";
import PasswordRequirements from "./PasswordRequirements";
import { isPasswordValid } from "../../application/passwordRules";
import { sortedCountries } from "../../../../shared/domain/countries";

export default function SignUpForm() {
  const { t, i18n } = useTranslation();
  const { setTherapist } = useTherapist();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // Form State
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState("es");
  const [patientTerm, setPatientTerm] = useState("patient");
  const [country, setCountry] = useState("ES");
  // Any country: where a therapist practises is theirs to say. The language is
  // not — the product is written in Spanish and English only.
  const countryOptions = useMemo(() => sortedCountries(i18n?.language), [i18n?.language]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfaData, setMfaData] = useState<{ qr_code_base64: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") ?? undefined;
  // Set when the visitor arrived from a centre's join link. Carried through
  // registration so they land inside the centre that invited them rather than
  // as a solo therapist who then has to find their way in.
  const centerInviteToken = searchParams.get("center_invite") ?? undefined;

  // Cooldown countdown for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const isFormValid = isPasswordValid(password) && isChecked;

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }

    setErrorMessage(null);
    setLoading(true);

    try {
      const data = { first_name: firstName, last_name: lastName, email, password, country, language, patient_term: patientTerm, invitation_token: inviteToken, center_invitation_token: centerInviteToken };
      const response = await registerTherapist(data);
      setMfaData(response.mfa);

      await openSession(email, password);
      await sendVerificationEmail();
      setResendCooldown(60);

      setStep(3);
    } catch (err: unknown) {
      console.error("Error during sign up:", err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any;

      if (error?.response?.status === 409 || error?.code === "auth/email-already-in-use") {
        setErrorMessage(t("auth.errorExists"));
      } else if (error?.response?.status === 400 || error?.code === "auth/invalid-email") {
        setErrorMessage(t("auth.errorData"));
      } else {
        setErrorMessage(t("auth.errorReg"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      // Enables MFA, then refreshes the token so the session carries the claim.
      const therapist = await enableMfaForSignedInUser(totpCode);

      // Update local state so modals/UI reflect MFA is enabled immediately with official backend data
      setTherapist(therapist);

      navigate("/subscription");
    } catch {
      setErrorMessage(t("auth.errorInvalidMfa"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    if (!hasSignedInUser()) {
      setErrorMessage(t("auth.emailVerification.sessionExpired"));
      return;
    }
    setErrorMessage(null);
    setResendNotice(null);
    setResendLoading(true);
    try {
      await sendVerificationEmail();
      setResendCooldown(60);
      setResendNotice(t("auth.emailVerification.resendSuccess"));
    } catch {
      setErrorMessage(t("auth.emailVerification.resendError"));
    } finally {
      setResendLoading(false);
    }
  };

  const handleConfirmEmailVerified = async () => {
    if (!hasSignedInUser()) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      if (await reloadEmailVerified()) {
        sendWelcomeEmail().catch(() => {/* fire-and-forget */});
        if (centerInviteToken) {
          // Joining waits for a verified address: the server refuses it
          // before. A failure leaves the link usable from the invitation page.
          try {
            await refreshIdToken();
            await acceptCenterInvitation(centerInviteToken);
          } catch (err) {
            console.error("Could not join the centre", err);
          }
        }
        setStep(4);
      } else {
        setErrorMessage(t("auth.emailVerification.notVerifiedYet"));
      }
    } finally {
      setLoading(false);
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        {inviteToken && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 text-brand-700 dark:text-brand-300 text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            {t("invite.plusBadge", "Plus plan included")}
          </div>
        )}
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold text-content-primary">
            {t("auth.joinTitle")}
          </h1>
          <div className="flex gap-1">
            <div className={`h-1.5 w-6 rounded-full transition-all duration-300 ${step === 1 ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-800"}`} />
            <div className={`h-1.5 w-6 rounded-full transition-all duration-300 ${step === 2 ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-800"}`} />
            <div className={`h-1.5 w-6 rounded-full transition-all duration-300 ${step === 3 ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-800"}`} />
            <div className={`h-1.5 w-6 rounded-full transition-all duration-300 ${step === 4 ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-800"}`} />
          </div>
        </div>
        <p className="text-content-tertiary">
          {step === 1 ? t("auth.joinSubtitle") : step === 2 ? t("auth.securityTitle") : step === 3 ? t("auth.emailVerification.title") : t("auth.mfaTitle")}
        </p>
      </div>

      <div className="relative overflow-hidden min-h-[400px]">
        <AnimatePresence initial={false} custom={step === 1 ? -1 : 1}>
          <motion.div
            key={step}
            custom={step === 1 ? -1 : 1}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="w-full"
          >
            {step === 1 && (
              /* STEP 1: PERSONAL INFO */
              <form onSubmit={handleSignUp} className="space-y-6">
                {errorMessage && (
                  <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4" />
                    {errorMessage}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fname">{t("auth.firstName")}*</Label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                      <Input
                        id="fname"
                        name="fname"
                        className="pl-10"
                        placeholder={t("auth.enterFirstName")}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lname">{t("auth.lastName")}*</Label>
                    <Input
                      id="lname"
                      name="lname"
                      placeholder={t("auth.enterLastName")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}*</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      className="pl-10"
                      placeholder={t("auth.enterEmail")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">{t("auth.country")}*</Label>
                    <select
                      id="country"
                      className="w-full h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 focus:border-brand-500 focus:ring-2 focus:ring-brand-500 outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                    >
                      {countryOptions.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language">{t("auth.language")}*</Label>
                    <select
                      id="language"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 focus:border-brand-500 focus:ring-2 focus:ring-brand-500 outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      required
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patientTerm">{t("profile.patientTerm.label")}</Label>
                  <select
                    id="patientTerm"
                    value={patientTerm}
                    onChange={(e) => setPatientTerm(e.target.value)}
                    className="w-full h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 focus:border-brand-500 focus:ring-2 focus:ring-brand-500 outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  >
                    <option value="patient">{t("profile.patientTerm.patient")}</option>
                    <option value="client">{t("profile.patientTerm.client")}</option>
                    <option value="consultee">{t("profile.patientTerm.consultee")}</option>
                  </select>
                </div>

                <div className="pt-4 space-y-4">
                  <Button
                    className="w-full group"
                    type="submit"
                    disabled={!firstName || !lastName || !email}
                    endIcon={
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    }
                  >
                    {t("common.next")}
                  </Button>
                  <div className="flex items-center gap-3">
                    <hr className="flex-1 border-gray-200 dark:border-white/[0.08]" />
                    <span className="text-xs text-content-tertiary font-medium">{t("common.or", "or")}</span>
                    <hr className="flex-1 border-gray-200 dark:border-white/[0.08]" />
                  </div>
                  <GoogleSignInButton mode="signUp" />
                </div>
              </form>
            )}

            {step === 2 && (
              /* STEP 2: SECURITY */
              <form onSubmit={handleSignUp} className="space-y-6">
                {errorMessage && (
                  <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4" />
                    {errorMessage}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}*</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      className="pr-12"
                      placeholder={t("auth.enterPassword")}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-content-tertiary hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                    >
                      {showPassword ? <Eye className="w-5 h-5 text-content-tertiary" /> : <EyeOff className="w-5 h-5 text-content-tertiary" />}
                    </button>
                  </div>

                  <PasswordRequirements password={password} />
                </div>

                <div className="flex items-start gap-3 p-1">
                  <Checkbox
                    id="terms"
                    checked={isChecked}
                    onChange={setIsChecked}
                    className="mt-1"
                  />
                  <label htmlFor="terms" className="text-sm text-content-tertiary leading-relaxed cursor-pointer select-none">
                    <Trans
                      i18nKey="auth.terms"
                      components={{
                        1: <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-500 font-bold hover:underline" />,
                        3: <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-500 font-bold hover:underline" />
                      }}
                    />
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1"
                    startIcon={<ChevronLeft className="w-4 h-4" />}
                  >
                    {t("common.previous")}
                  </Button>
                  <Button
                    className="flex-[2]"
                    type="submit"
                    disabled={!isFormValid || loading}
                    loading={loading}
                    startIcon={<ShieldCheck className="w-4 h-4" />}
                  >
                    {t("auth.signUp")}
                  </Button>
                </div>
              </form>
            )}

            {step === 3 && (
              /* STEP 3: EMAIL VERIFICATION */
              <div className="space-y-6">
                <div className="flex flex-col items-center gap-3 p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t("auth.emailVerification.subtitle", { email })}
                  </p>
                </div>

                {errorMessage && (
                  <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4" />
                    {errorMessage}
                  </div>
                )}

                {resendNotice && (
                  <div className="p-3 rounded-xl bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/20 text-success-600 dark:text-success-400 text-sm flex gap-2 items-center">
                    <ShieldCheck className="w-4 h-4" />
                    {resendNotice}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full"
                    onClick={handleConfirmEmailVerified}
                    loading={loading}
                    startIcon={<ShieldCheck className="w-4 h-4" />}
                  >
                    {t("auth.emailVerification.confirm")}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendCooldown > 0 || resendLoading}
                    loading={resendLoading}
                  >
                    {resendCooldown > 0
                      ? t("auth.emailVerification.resendIn", { seconds: resendCooldown })
                      : t("auth.emailVerification.resend")}
                  </Button>
                </div>
              </div>
            )}

            {step === 4 && (
              /* STEP 4: MFA SETUP */
              <div className="space-y-6">
                <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                  <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-300">
                    {t("profile.mfa.qrCodeDesc")}
                  </p>
                  {mfaData?.qr_code_base64 && (
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <img src={`data:image/png;base64,${mfaData.qr_code_base64}`} alt="MFA QR Code" className="w-40 h-40" />
                    </div>
                  )}
                  <Link
                    to="/google-authenticator-guide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400 hover:underline mt-2"
                  >
                    {t("profile.mfa.guideLink")}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mfaCode">
                    {t("profile.mfa.enterCode")}
                  </Label>
                  <Input
                    id="mfaCode"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="000 000"
                    className="text-center text-2xl tracking-widest font-bold"
                  />
                </div>

                {errorMessage && (
                  <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4" />
                    {errorMessage}
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={handleVerifyMfa}
                  loading={loading}
                  disabled={totpCode.length < 6}
                  startIcon={<ShieldCheck className="w-4 h-4" />}
                >
                  {t("profile.mfa.verifyAndEnable")}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    {t("auth.skipMfa")}
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-8 pt-8 border-t border-gray-100 dark:border-white/[0.05]">
        <p className="text-sm font-medium text-center text-content-tertiary">
          {t("auth.alreadyAccount")}{" "}
          <Link
            to="/login"
            className="text-brand-600 dark:text-brand-400 font-bold hover:underline ml-1"
          >
            {t("auth.signIn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
